import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { Input, Alert } from '@components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';
import {
  AuthSplitShell,
  CTA_BUTTON,
  FIELD_INPUT,
  FIELD_INPUT_ERROR,
  FIELD_LABEL,
  GHOST_BUTTON,
} from './AuthSplitShell';
import { authService } from '../services/authService';
import { PASSWORD_COMPLEXITY_REGEX, PASSWORD_COMPLEXITY_MESSAGE } from '@lib/utils/passwordValidation';

const setNewPasswordSchema = z
  .object({
    password: z.string().regex(PASSWORD_COMPLEXITY_REGEX, PASSWORD_COMPLEXITY_MESSAGE),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type SetNewPasswordFormData = z.infer<typeof setNewPasswordSchema>;

/**
 * Pantalla de destino del link de invitación/creación de usuario.
 * Aludra le agrega automáticamente /{userId}/{tenantId}/{token} al
 * RedirectEmail que le mandamos (ver user.controller.ts::buildSetPasswordRedirectUrl).
 * Distinta de ResetPasswordPage (self-service "olvidé mi contraseña").
 *
 * Presentación: usa el mismo shell que /login y /forgot-password
 * (`AuthSplitShell`). Antes era una `Card` centrada sobre fondo gris con el
 * logo suelto arriba — el diseño anterior del portal —, así que quien recibía
 * la invitación aterrizaba en la única pantalla del flujo de acceso que no se
 * parecía a las demás. La lógica (validación, mutación, estados de enlace
 * inválido y de éxito) no cambió: solo el envoltorio.
 */
export function SetNewPasswordPage() {
  const navigate = useNavigate();
  const { userId, tenantId, token } = useParams<{ userId: string; tenantId: string; token: string }>();
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetNewPasswordFormData>({
    resolver: zodResolver(setNewPasswordSchema),
  });

  const mutation = useMutation({
    mutationFn: (data: SetNewPasswordFormData) => {
      if (!userId || !tenantId || !token) {
        throw new Error('El enlace es inválido o está incompleto.');
      }
      return authService.confirmEmail(tenantId, userId, data.password, data.confirmPassword, token);
    },
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    },
  });

  const onSubmit = (data: SetNewPasswordFormData) => {
    mutation.mutate(data);
  };

  const linkIsValid = !!(userId && tenantId && token);

  return (
    <AuthSplitShell
      kicker="Activación de cuenta"
      grainId="set-new-password"
      logoTestId="auth-set-new-password-page-tenant-logo-image"
    >
      {!linkIsValid ? (
        <>
          <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
            Enlace inválido
          </h2>
          <p className="mb-10 mt-3 text-[15px] leading-[1.55] text-secondary-500 short:mb-5 short:mt-1.5">
            El enlace de invitación es inválido o está incompleto. Solicita que te reenvíen la
            invitación.
          </p>

          <Alert variant="danger" title="Enlace Inválido" data-testid="auth-set-new-password-invalid-link-alert">
            Falta información en el enlace: pídele a quien te invitó que lo genere de nuevo.
          </Alert>

          <Link to="/login" className="mt-6 block">
            <span className={GHOST_BUTTON} data-testid="auth-set-new-password-back-to-login-button">
              <ArrowLeftIcon className="h-4 w-4" />
              Volver al inicio de sesión
            </span>
          </Link>
        </>
      ) : success ? (
        <>
          <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
            Cuenta activada
          </h2>
          <p className="mb-10 mt-3 text-[15px] leading-[1.55] text-secondary-500 short:mb-5 short:mt-1.5">
            Tu contraseña quedó establecida. Te llevamos al inicio de sesión…
          </p>

          <Alert variant="success" title="Contraseña Establecida" data-testid="auth-set-new-password-success-alert">
            Tu contraseña ha sido establecida exitosamente. Redirigiendo al login...
          </Alert>
        </>
      ) : (
        <>
          <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
            Establecer Contraseña
          </h2>
          <p className="mb-10 mt-3 text-[15px] leading-[1.55] text-secondary-500 short:mb-5 short:mt-1.5">
            Elige tu contraseña para activar la cuenta y entrar al portal.
          </p>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 short:space-y-4"
            data-testid="auth-set-new-password-form"
          >
            {mutation.error && (
              <Alert variant="danger" title="Error" data-testid="auth-set-new-password-error-alert">
                {(mutation.error as AxiosError<{ message?: string }>)?.response?.data?.message ||
                  'El enlace es inválido o expiró. Solicita uno nuevo.'}
              </Alert>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="set-new-password-password" className={FIELD_LABEL}>
                Nueva Contraseña
              </label>
              <Input
                id="set-new-password-password"
                type="password"
                placeholder="••••••••"
                error={errors.password?.message}
                helperText="Mínimo 8 caracteres, con mayúscula, minúscula y carácter especial"
                data-testid="auth-set-new-password-password-input"
                className={cn(FIELD_INPUT, errors.password && FIELD_INPUT_ERROR)}
                {...register('password')}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="set-new-password-confirm" className={FIELD_LABEL}>
                Confirmar Contraseña
              </label>
              <Input
                id="set-new-password-confirm"
                type="password"
                placeholder="••••••••"
                error={errors.confirmPassword?.message}
                data-testid="auth-set-new-password-confirm-password-input"
                className={cn(FIELD_INPUT, errors.confirmPassword && FIELD_INPUT_ERROR)}
                {...register('confirmPassword')}
              />
            </div>

            <button
              type="submit"
              disabled={mutation.isPending}
              className={CTA_BUTTON}
              data-testid="auth-set-new-password-submit-button"
            >
              <span>{mutation.isPending ? 'Estableciendo…' : 'Establecer Contraseña'}</span>
              {mutation.isPending ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                <svg
                  className="cta-arrow"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              )}
            </button>

            <Link to="/login" className="block">
              <span className={GHOST_BUTTON} data-testid="auth-set-new-password-back-to-login-button">
                <ArrowLeftIcon className="h-4 w-4" />
                Volver al inicio de sesión
              </span>
            </Link>
          </form>
        </>
      )}
    </AuthSplitShell>
  );
}
